// Imperative overlay drawn into the snapshot iframe's own document, never by React.

import type {
  OverlayBox,
  OverlayHandlers,
  OverlayPin,
  OverlaySelection,
  SnapshotOverlaySeverityColors,
} from './siteHealthInspector.types'

// Iframe content cannot read the app's Tailwind classes, so severity is literal hex.
export const OVERLAY_SEVERITY_COLORS: SnapshotOverlaySeverityColors = {
  critical: '#ef4444',
  warning: '#f59e0b',
  info: '#94a3b8',
}

const OVERLAY_ATTRIBUTE = 'data-sh-overlay'
const DIM_COLOR = 'rgba(2, 6, 23, 0.55)'
// The pin sits outside the element it marks, so it never covers the first characters.
const PIN_GAP = 6
// A dark halo outside the white border keeps a pin findable on any page background.
const PIN_HALO =
  '0 0 0 1.5px rgba(2, 6, 23, 0.85), 0 1px 4px rgba(0, 0, 0, 0.4)'
const POPOVER_TEXT_COLOR = '#f8fafc'
// A target inside this band from either edge already reads, so it stays put.
const REVEAL_BAND_RATIO = 0.18
const REVEAL_MIN_MARGIN = 64
// Where a revealed target lands, measured down from the top of the frame.
const REVEAL_OFFSET_RATIO = 0.3
const PIN_SIZE = 20
const SELECTION_SELECTOR = `[${OVERLAY_ATTRIBUTE}="dim"], [${OVERLAY_ATTRIBUTE}="ring"], [${OVERLAY_ATTRIBUTE}="popover"]`
const Z_DIM = 2147483000
const Z_RING = 2147483001
const Z_POPOVER = 2147483002
const Z_PIN = 2147483003
// Mirrors --font-sans so the overlay matches the surrounding application.
const FONT_STACK =
  '"Work Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif'

// Keyed by document so the popover's Details button reaches the parent handlers.
const documentHandlers = new WeakMap<Document, OverlayHandlers>()

function resolveTarget(doc: Document, selector: string): Element | null {
  try {
    return doc.querySelector(selector)
  } catch {
    return null
  }
}

function boxOf(doc: Document, element: Element): OverlayBox {
  const rect = element.getBoundingClientRect()
  // A snapshot without a doctype renders in quirks mode, where body scrolls, not documentElement.
  const root = doc.scrollingElement ?? doc.documentElement

  return {
    top: rect.top + root.scrollTop,
    left: rect.left + root.scrollLeft,
    width: rect.width,
    height: rect.height,
  }
}

function createOverlayNode(
  doc: Document,
  tag: 'div' | 'button',
  role: string
): HTMLElement {
  const node = doc.createElement(tag)

  node.setAttribute(OVERLAY_ATTRIBUTE, role)
  node.style.fontFamily = FONT_STACK
  node.style.boxSizing = 'content-box'

  return node
}

/**
 * Places the pin outside the element it marks: to its left, else above it.
 * A pin centred on the corner covers the element's own first characters.
 */
function pinPosition(box: OverlayBox): { top: number; left: number } {
  const beside = box.left - PIN_SIZE - PIN_GAP

  if (beside >= 0) return { top: Math.max(0, box.top), left: beside }

  const above = box.top - PIN_SIZE - PIN_GAP

  if (above >= 0) return { top: above, left: Math.max(0, box.left) }

  return { top: Math.max(0, box.top), left: Math.max(0, box.left) }
}

function createPin(
  doc: Document,
  target: Element,
  overlayPin: OverlayPin
): HTMLElement {
  const box = boxOf(doc, target)
  const position = pinPosition(box)
  const node = createOverlayNode(doc, 'button', 'pin')
  node.textContent = String(overlayPin.position)
  node.title = overlayPin.note

  node.setAttribute('type', 'button')
  node.setAttribute('aria-label', `Finding ${overlayPin.position}`)

  Object.assign(node.style, {
    position: 'absolute',
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${PIN_SIZE}px`,
    height: `${PIN_SIZE}px`,
    zIndex: String(Z_PIN),
    borderRadius: '9999px',
    border: '2px solid #ffffff',
    background: OVERLAY_SEVERITY_COLORS[overlayPin.severity],
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '600',
    lineHeight: `${PIN_SIZE - 4}px`,
    textAlign: 'center',
    padding: '0',
    cursor: 'pointer',
    boxShadow: PIN_HALO,
  })

  return node
}

/**
 * Dims the page with a hole over the selected element. A flat overlay covered the
 * element too, which buried the very thing the reader clicked.
 */
function createDimLayer(doc: Document, box: OverlayBox): HTMLElement {
  const node = createOverlayNode(doc, 'div', 'dim')

  Object.assign(node.style, {
    position: 'absolute',
    top: `${box.top}px`,
    left: `${box.left}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    zIndex: String(Z_DIM),
    boxShadow: `0 0 0 9999px ${DIM_COLOR}`,
    pointerEvents: 'none',
  })

  return node
}

function createRing(
  doc: Document,
  box: OverlayBox,
  color: string
): HTMLElement {
  const node = createOverlayNode(doc, 'div', 'ring')

  Object.assign(node.style, {
    position: 'absolute',
    top: `${box.top - 2}px`,
    left: `${box.left - 2}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    zIndex: String(Z_RING),
    border: `2px solid ${color}`,
    borderRadius: '6px',
    boxShadow: `0 0 0 6px ${color}33`,
    pointerEvents: 'none',
    transition: 'top 120ms ease-out, left 120ms ease-out',
  })

  return node
}

function createDetailsButton(
  doc: Document,
  selection: OverlaySelection
): HTMLElement {
  const button = createOverlayNode(doc, 'button', 'details')
  button.textContent = 'Details'

  button.setAttribute('type', 'button')

  Object.assign(button.style, {
    border: '0',
    borderRadius: '6px',
    background: OVERLAY_SEVERITY_COLORS[selection.severity],
    color: '#ffffff',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  })

  // Details opens the panel row. A toggle here would close a row that is already open.
  button.addEventListener('click', () => {
    documentHandlers
      .get(doc)
      ?.openDetails(selection.findingId, selection.position)
  })

  return button
}

function createNote(doc: Document, text: string): HTMLElement {
  const note = createOverlayNode(doc, 'div', 'note')
  note.textContent = text

  Object.assign(note.style, {
    margin: '0 0 8px',
    color: POPOVER_TEXT_COLOR,
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    textAlign: 'left',
  })

  return note
}

function createPopover(
  doc: Document,
  box: OverlayBox,
  selection: OverlaySelection
): HTMLElement {
  const node = createOverlayNode(doc, 'div', 'popover')

  Object.assign(node.style, {
    position: 'absolute',
    top: `${box.top + box.height + 12}px`,
    left: `${box.left}px`,
    maxWidth: '320px',
    zIndex: String(Z_POPOVER),
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    background: '#0f172a',
    color: POPOVER_TEXT_COLOR,
    padding: '10px 12px',
    fontSize: '12px',
    lineHeight: '18px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
  })

  if (selection.note) {
    node.appendChild(createNote(doc, selection.note))
  }

  node.appendChild(createDetailsButton(doc, selection))

  return node
}

function removeNodes(doc: Document, selector: string): void {
  Array.from(doc.querySelectorAll(selector)).forEach((node) => {
    node.remove()
  })
}

// Draws one numbered pin per resolvable finding target, skipping a selector that no longer matches.
export function mountPins(
  doc: Document,
  pins: OverlayPin[],
  handlers: OverlayHandlers
): void {
  documentHandlers.set(doc, handlers)
  removeNodes(doc, `[${OVERLAY_ATTRIBUTE}="pin"]`)

  pins.forEach((overlayPin) => {
    const target = resolveTarget(doc, overlayPin.selector)

    if (!target) return

    const node = createPin(doc, target, overlayPin)

    node.addEventListener('click', () => {
      handlers.select(overlayPin.findingId, overlayPin.position)
    })
    doc.body.appendChild(node)
  })
}

/** Dims the page, rings the selected element, and explains it in a popover. */
export function applySelection(
  doc: Document,
  selection: OverlaySelection | null
): void {
  removeNodes(doc, SELECTION_SELECTOR)

  if (!selection) return

  const target = resolveTarget(doc, selection.selector)

  if (!target) return

  const box = boxOf(doc, target)
  const color = OVERLAY_SEVERITY_COLORS[selection.severity]

  doc.body.appendChild(createDimLayer(doc, box))
  doc.body.appendChild(createRing(doc, box, color))
  doc.body.appendChild(createPopover(doc, box, selection))
}

/**
 * Scrolls the selected element into view inside the snapshot document, with room
 * around it. A target already inside the comfortable band does not move, so a
 * pin click never jolts the frame.
 */
export function revealSelection(
  doc: Document,
  selection: OverlaySelection | null
): void {
  if (!selection) return

  const view = doc.defaultView

  if (!view) return

  const target = resolveTarget(doc, selection.selector)

  if (!target) return

  const rect = target.getBoundingClientRect()
  const viewportHeight = view.innerHeight
  const margin = Math.max(REVEAL_MIN_MARGIN, viewportHeight * REVEAL_BAND_RATIO)

  if (rect.top >= margin && rect.bottom <= viewportHeight - margin) return

  const top = rect.top + view.scrollY - viewportHeight * REVEAL_OFFSET_RATIO

  view.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

/** Removes every node this module injected into the snapshot document. */
export function clearOverlay(doc: Document): void {
  removeNodes(doc, `[${OVERLAY_ATTRIBUTE}]`)
  documentHandlers.delete(doc)
}
